import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from './api';
import { SOCIAL_LINKS } from './siteConfig';
import bakedSummary from '../data/reviewSummary.json';

/*
 * Review summary shared by the header (Reviews menu item once there are 3+ reviews), the footer
 * and the trust bar (Google rating line) and the /review + /reviews pages (links).
 *
 * The values live in Mongo. The pre-rendered HTML and the first client render both start from the
 * summary BAKED at build time (src/data/reviewSummary.json, refreshed from /api/reviews/summary by
 * seo/fetch-build-data.js — Prompt 13: the snapshot carries the real rating line, not an empty default),
 * so nothing here can cause a hydration mismatch; the live summary is then fetched once after mount.
 * If the build could not reach the API the committed JSON is the (honest, empty) fallback.
 */
const DEFAULT_STATS = {
  googleRating: null,
  googleCount: null,
  googleReviewLink: SOCIAL_LINKS.googleReview,
  googleMapsUrl: SOCIAL_LINKS.googleMaps,
  yelpUrl: SOCIAL_LINKS.yelp,
  showRatingLine: false,
};

const DEFAULT_SUMMARY = {
  loaded: false,
  count: Number(bakedSummary?.count) || 0,
  stats: { ...DEFAULT_STATS, ...((bakedSummary && bakedSummary.stats) || {}) },
};

const SiteDataContext = createContext(DEFAULT_SUMMARY);

export const SiteDataProvider = ({ children }) => {
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  useEffect(() => {
    let alive = true;
    api.get('/reviews/summary')
      .then(({ data }) => {
        if (!alive) return;
        setSummary({
          loaded: true,
          count: Number(data?.count) || 0,
          stats: { ...DEFAULT_STATS, ...(data?.stats || {}) },
        });
      })
      .catch(() => { if (alive) setSummary((s) => ({ ...s, loaded: true })); });
    return () => { alive = false; };
  }, []);
  return <SiteDataContext.Provider value={summary}>{children}</SiteDataContext.Provider>;
};

export const useReviewSummary = () => useContext(SiteDataContext);

/** Reviews joins the main menu only once there are three or more on-site reviews (brief Part 3.4). */
export const REVIEWS_MENU_MIN = 3;

/** "4.9" / "5.0" — exactly as entered by the owner, never rounded up or combined with Yelp. */
export const formatRating = (rating) => {
  const n = Number(rating);
  if (!Number.isFinite(n)) return '';
  return Number.isInteger(n) ? `${n}.0` : String(n);
};

/**
 * "{googleRating}★ on Google ({googleCount} reviews)" — rendered ONLY when the owner has entered a
 * Google count of 1 or more (and a rating). Always names the source; links to the Maps listing.
 */
export const GoogleRatingLine = ({ className = '', testId = 'google-rating-line' }) => {
  const { stats } = useReviewSummary();
  if (!stats?.showRatingLine) return null;
  const count = Number(stats.googleCount);
  return (
    <a
      href={stats.googleMapsUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1.5 whitespace-nowrap ${className}`}
      data-testid={testId}
      aria-label={`${formatRating(stats.googleRating)} stars on Google from ${count} ${count === 1 ? 'review' : 'reviews'} (opens Google Maps)`}
    >
      <span className="font-semibold">{formatRating(stats.googleRating)}★</span>
      <span>on Google ({count} {count === 1 ? 'review' : 'reviews'})</span>
    </a>
  );
};
